import type {
  ResizeEvent,
} from '@interactjs/types';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from 'common/components/Elements/Button/Button';
import Icon from 'common/components/Elements/Icon/Icon';
import { scrollToPosition } from 'common/utilities/browser/scroll/scroll';
import { formatStyleTranslate,getElStyleTranslate } from 'common/utilities/css/css';
import useScroll from 'common/utilities/hooks/useScroll/useScroll';
import useViewportSize from 'common/utilities/hooks/useViewportSize/useViewportSize';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import type { AppDispatch } from 'core/redux/store/configureStore.types';
import theme, { ThemeColors, ThemeVars } from 'core/utilities/theme';
import interact from 'interactjs';
import React, {
  useCallback, useEffect, useState,
} from 'react';
import type { ControlPosition } from 'react-draggable';
import Draggable from 'react-draggable';
import { useDispatch, useSelector } from 'react-redux';
import type { EntitySequence as Sequence, SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { ActiveViewPosition } from 'sequenceAnalyser/components/SequencesNavigator/SequencesNavigator';
import SequencesNavigator from 'sequenceAnalyser/components/SequencesNavigator/SequencesNavigator';
import { TABLE_ACTIONS_HEIGHT } from 'sequenceAnalyser/components/TableActions/TableActionsHeader/TableActionsHeader.style';
import { updateShowSequenceNavigatorSetting } from 'sequenceAnalyser/redux/actions/sequenceAnalyserActions';
import type { SelectedColorScheme } from 'sequenceAnalyser/utilities/colors';
import { getVisibleSequencesFeatures } from 'sequenceAnalyser/utilities/features/features';
import { RULER_PIXEL_HEIGHT, sequencesChunkHeight, sequencesChunkWidth } from 'sequenceAnalyser/utilities/screen/screen';
import type { FeatureDefinitions } from 'sequenceAnalyser/utilities/sequenceAnalyser.types';
import { hasAnyFeatureDefinitionVisible } from 'sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser';

interface NavigatorDimension {
  width: number;
  height: number;
  transform?: string;
}
interface ComponentProps {
  alignedSequences: SequenceAlign[];
  sequences: Sequence[];
  featureDefinitions: FeatureDefinitions;
  colors?: SelectedColorScheme;
  expandedFeaturesForSequences: number[];
}

function SequencesNavigatorPosition({
  alignedSequences, sequences, featureDefinitions, colors, expandedFeaturesForSequences,
}: ComponentProps) {
  const dispatch = useDispatch<AppDispatch>();

  const sidebarPixelWidth = useSelector((state: ApplicationState) => state.sequenceAnalyser.sidebarPixelWidth);
  const zoomLevel = useSelector((state: ApplicationState) => state.sequenceAnalyser.zoomLevel);
  const showAppHeaderEnv = useSelector((state: ApplicationState) => state.ui.showAppHeaderEnv);
  const showSequencesNavigator = useSelector((state: ApplicationState) => (
    state.sequenceAnalyser.settings.showSequencesNavigator
  ));
  const selection = useSelector((state: ApplicationState) => state.sequenceAnalyser.selection);

  const [draggablePosition, setDraggablePosition] = useState<ControlPosition | undefined>();

  const hasColors = colors !== undefined;
  const hasFeatures = hasAnyFeatureDefinitionVisible(featureDefinitions);
  const visibleSequencesFeatures = getVisibleSequencesFeatures(sequences, featureDefinitions);

  // Calculate viewport height offset
  let defaultTop = ThemeVars.headerMenuHeight;
  if (showAppHeaderEnv) defaultTop += ThemeVars.headerEnvHeight;
  defaultTop += TABLE_ACTIONS_HEIGHT;
  defaultTop += RULER_PIXEL_HEIGHT;

  const viewportSize = useViewportSize();
  const viewportWidth = viewportSize.width - sidebarPixelWidth;
  const viewportHeight = viewportSize.height - defaultTop;

  const sequencesWidth = sequencesChunkWidth(alignedSequences, 0, zoomLevel);

  const sequencesHeight = sequencesChunkHeight(
    alignedSequences,
    hasColors,
    hasFeatures,
    visibleSequencesFeatures,
    expandedFeaturesForSequences,
    zoomLevel,
  );

  const navigatorPadding = ThemeVars.spacing;

  /**
   * Calculate default dimensions height: ideally 3 pixels per sequences in the navigator.
   * Min 200px, max 200px as default.
   */
  let navigatorDimensionHeight = alignedSequences.length * 3;
  if (navigatorDimensionHeight > 300) navigatorDimensionHeight = 300;
  if (navigatorDimensionHeight < 200) navigatorDimensionHeight = 200;

  const [navigatorDimension, setNavigatorDimension] = useState<NavigatorDimension>({
    width: 200,
    height: navigatorDimensionHeight,
  });

  /**
   * Handle moving within the navigator
   */
  const scroll = useScroll();

  /** Handle cases where the sequences are smaller than the viewport */
  const maxViewportHeight = viewportHeight > sequencesHeight ? sequencesHeight : viewportHeight;
  /** Handle cases where the sequences are shorter than the viewport */
  const maxViewportWidth = viewportWidth > sequencesWidth ? sequencesWidth : viewportWidth;

  const calcActiveViewPosition = useCallback((): ActiveViewPosition => ({
    x: (scroll.x * (navigatorDimension.width - navigatorPadding)) / sequencesWidth,
    y: (scroll.y * (navigatorDimension.height - navigatorPadding)) / sequencesHeight,
    width: (maxViewportWidth * (navigatorDimension.width - navigatorPadding)) / sequencesWidth,
    height: (maxViewportHeight * (navigatorDimension.height - navigatorPadding)) / sequencesHeight,
  }), [
    navigatorDimension.height, navigatorDimension.width,
    scroll.x, scroll.y,
    sequencesHeight, sequencesWidth,
    maxViewportHeight, maxViewportWidth,
    navigatorPadding,
  ]);

  const [activeViewPosition, setActiveViewPosition] = useState<ActiveViewPosition>(calcActiveViewPosition());

  const handleActiveViewPositionChange = (newActiveViewPosition: ActiveViewPosition) => {
    setActiveViewPosition(newActiveViewPosition);

    scrollToPosition(
      (sequencesHeight * newActiveViewPosition.y) / navigatorDimension.height,
      (sequencesWidth * newActiveViewPosition.x) / navigatorDimension.width,
    );
  };

  useEffect(() => {
    setActiveViewPosition(calcActiveViewPosition());
  }, [calcActiveViewPosition]);

  /**
   * Handle resizing the navigator
   */
  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    interact('.resizableNavigatorWrapper')
      .resizable({
        edges: {
          top: true, left: true, bottom: true, right: true,
        },
        invert: 'reposition',
        listeners: {
          start() {
            setResizing(true);
          },
          end(event: ResizeEvent) {
            setResizing(false);

            const { x, y } = event.target.dataset;

            const parentEl = event.target.parentElement;

            if (!parentEl) return;

            const { translateX, translateY } = getElStyleTranslate(parentEl);

            const adjustedTranslateX = translateX + (parseFloat(x || '') || 0);
            const adjustedTranslateY = translateY + (parseFloat(y || '') || 0);

            parentEl.style.transform = formatStyleTranslate(adjustedTranslateX, adjustedTranslateY);

            Object.assign(event.target.dataset, { x: 0, y: 0 });

            // Reset the parent container transform value and adjust the draggable element position accordingly
            setNavigatorDimension((prevState) => ({
              ...prevState,
              transform: undefined,
            }));

            setDraggablePosition({
              x: adjustedTranslateX,
              y: adjustedTranslateY,
            });
          },
          move(event: ResizeEvent) {
            const newWidth = event.rect.width;
            const newHeight = event.rect.height;

            // Adjust the position of the element if we pull from the bottom/right
            // as we don't want it to expand towards top/left
            const { x, y } = event.target.dataset;
            const finalX = (parseFloat(x || '') || 0) + (event.deltaRect?.right || 0);
            const finalY = (parseFloat(y || '') || 0) + (event.deltaRect?.bottom || 0);

            Object.assign(event.target.dataset, { x: finalX, y: finalY });

            setNavigatorDimension({
              width: newWidth,
              height: newHeight,
              transform: formatStyleTranslate(finalX, finalY),
            });
          },
        },
      });
  }, []);

  if (!showSequencesNavigator) return null;

  return (
    <Box sx={{
      position: 'fixed',
      width: '100vw',
      height: '100vh',
      top: 0,
      left: 0,
      transform: navigatorDimension.transform,
      zIndex: theme.zIndex.modal - 1,
      pointerEvents: 'none',
    }}
    >
      <Box sx={{
        position: 'relative',
        width: '100vw',
        height: 'calc(100vh - 30px)',
        top: '30px',
        pointerEvents: 'none',
      }}
      >
        <Draggable
          axis="both"
          handle=".sequenceNavitatorDraggableHandle"
          bounds="parent"
          positionOffset={undefined}
          position={draggablePosition}
          onStop={(e, data) => setDraggablePosition({ x: data.x, y: data.y })}
        >
          <Box
            sx={{
              position: 'absolute',
              right: '88px',
              bottom: '18px',
              width: navigatorDimension.width,
              height: navigatorDimension.height,
              outline: `1px solid ${ThemeColors.borderColor}`,
              borderRadius: theme.spacing(0, 0, 0.5, 0.5),
              pointerEvents: 'auto',
              background: 'white',
            }}
          >
            <Box sx={{
              position: 'absolute',
              top: '-30px',
              left: '0',
              width: '100%',
              background: 'white',
              outline: `1px solid ${ThemeColors.borderColor}`,
              borderRadius: theme.spacing(0.5, 0.5, 0, 0),
            }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 0.25,
                pl: 0.5,
                boxShadow: theme.shadows[1],
              }}
              >
                <Box sx={{ flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                  <Typography
                    component="span"
                    variant="body2"
                    className="sequenceNavitatorDraggableHandle"
                    sx={{
                      display: 'inline-block',
                      width: '100%',
                      cursor: 'move',
                      fontWeight: theme.typography.fontWeightMedium,
                    }}
                  >
                    <Icon element={DragIndicatorIcon} mr />
                    Alignment Overview
                  </Typography>
                </Box>
                <Box sx={{ '& button': { color: theme.palette.text.primary } }}>
                  <Button
                    action="tertiary"
                    onClick={() => {
                      dispatch(updateShowSequenceNavigatorSetting(false));
                    }}
                    buttonIcon
                    small
                  >
                    <Icon element={CloseIcon} />
                  </Button>
                </Box>
              </Box>
            </Box>
            <Box className="resizableNavigatorWrapper" sx={{ padding: `${navigatorPadding / 2}px`, height: '100%' }}>
              {!resizing && (
                <SequencesNavigator
                  width={navigatorDimension.width - navigatorPadding}
                  height={navigatorDimension.height - navigatorPadding}
                  featureDefinitions={featureDefinitions}
                  sequences={sequences}
                  alignedSequences={alignedSequences}
                  activeViewPosition={activeViewPosition}
                  onActiveViewPositionChange={handleActiveViewPositionChange}
                  selection={selection}
                />
              )}
            </Box>
          </Box>
        </Draggable>
      </Box>
    </Box>
  );
}
export default SequencesNavigatorPosition;
